import { useCallback, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import { clampDateRange, filterPointsByDateRange } from '@BUP/utils/date-range'

const DEFAULT_GRANULARITY = 'months'

// antd v4 hands us moment objects; the utils work with native Dates. Normalize at
// the boundary so the hook's public `range` is always `[Date, Date] | null`.
const toNativeDate = value => {
  if (!value) return null
  if (value instanceof Date) return value
  if (typeof value.toDate === 'function') return value.toDate()
  return new Date(value)
}

/**
 * Default window for a chart: `defaultSpan[granularity]` periods wide, anchored to
 * the last point the payload actually contains (`bounds.end`) — never to "today",
 * because dashboard payloads end before now — floored at `bounds.start` and clamped
 * to `maxDays`. The span counts periods inclusively: 12 months ending on 2024-12-01
 * starts on 2024-01-01, so monthly (day-01) series keep exactly 12 points.
 */
const computeDefaultRange = ({ boundsStartMs, boundsEndMs, spanValue, granularity, maxDays }) => {
  if (boundsStartMs === null || boundsEndMs === null) return null

  const end = new Date(boundsEndMs)
  const floor = new Date(boundsStartMs)
  const candidateStart = spanValue
    ? moment.utc(end).subtract(spanValue - 1, granularity).toDate()
    : floor
  const start = candidateStart < floor ? floor : candidateStart
  const clamped = clampDateRange(start, end, maxDays)

  return [clamped.start, clamped.end]
}

/**
 * Session-only date-window state for ONE chart.
 *
 * Each chart instantiates its own hook, so ranges are independent by construction:
 * no shared context, nothing persisted (no URL, no localStorage). Reloading the
 * page returns the chart to its default window.
 *
 * The payload loads asynchronously, so `bounds` is null on the first render and the
 * default is applied by an effect instead of a `useState` initializer. That is the
 * async race the previous ad-hoc `sociogramYear` state had to patch after the fact.
 *
 * @param {object} options
 * @param {{start: Date, end: Date}|null|function} options.bounds window covered by
 *   the payload; pass a `(granularity) => bounds | null` function when the payload
 *   carries several granularities and each one spans a different window (Logins).
 * @param {number} options.maxDays clamp applied to every selection, in days
 * @param {object} [options.defaultSpan] per-granularity default width, e.g. { months: 12 }
 * @param {string[]} [options.granularities] e.g. ['days','weeks','months']; <=1 hides the toggle
 * @param {string} [options.defaultGranularity]
 */
export const useChartRange = ({
  bounds: boundsOrFn = null,
  maxDays,
  defaultSpan = null,
  granularities = [],
  defaultGranularity = DEFAULT_GRANULARITY
}) => {
  const [granularity, setGranularityState] = useState(defaultGranularity)
  const [range, setRangeState] = useState(null)
  const hasUserChosen = useRef(false)

  const bounds = typeof boundsOrFn === 'function' ? boundsOrFn(granularity) : boundsOrFn

  // Depend on primitives, not object identities: `defaultSpan` and `bounds` are
  // commonly inline literals at the call site, and an effect keyed on their
  // identities would re-run (and re-emit a fresh range array) on every render.
  const spanValue = defaultSpan ? defaultSpan[granularity] : undefined
  const boundsStartMs = bounds ? toNativeDate(bounds.start).getTime() : null
  const boundsEndMs = bounds ? toNativeDate(bounds.end).getTime() : null

  const defaultRange = useCallback(
    () => computeDefaultRange({ boundsStartMs, boundsEndMs, spanValue, granularity, maxDays }),
    [boundsStartMs, boundsEndMs, spanValue, granularity, maxDays]
  )

  // Apply the default window while the user has not chosen one. Re-runs when the
  // payload arrives or the granularity changes, but never clobbers a selection.
  useEffect(() => {
    if (hasUserChosen.current) return
    setRangeState(current => {
      const next = defaultRange()
      if (
        current && next &&
        current[0].getTime() === next[0].getTime() &&
        current[1].getTime() === next[1].getTime()
      ) {
        return current
      }
      return next
    })
  }, [defaultRange])

  const setRange = useCallback(
    nextRange => {
      if (!Array.isArray(nextRange) || !nextRange[0] || !nextRange[1]) {
        hasUserChosen.current = false
        setRangeState(defaultRange())
        return
      }

      const clamped = clampDateRange(
        toNativeDate(nextRange[0]),
        toNativeDate(nextRange[1]),
        maxDays
      )
      hasUserChosen.current = true
      setRangeState([clamped.start, clamped.end])
    },
    [defaultRange, maxDays]
  )

  // Switching granularity is a programmatic reset, not a user range choice: it
  // clears the guard so the effect re-derives the default window for the new
  // granularity.
  const setGranularity = useCallback(nextGranularity => {
    hasUserChosen.current = false
    setGranularityState(nextGranularity)
  }, [])

  const disabledDate = useCallback(
    current => {
      if (boundsStartMs === null || boundsEndMs === null || !current) return false
      return (
        current.isBefore(moment.utc(boundsStartMs), 'day') ||
        current.isAfter(moment.utc(boundsEndMs), 'day')
      )
    },
    [boundsStartMs, boundsEndMs]
  )

  const filterPoints = useCallback(
    points => {
      if (!range || !Array.isArray(points)) return Array.isArray(points) ? points : []
      return filterPointsByDateRange(points, range[0], range[1])
    },
    [range]
  )

  return {
    range,
    granularity,
    setRange,
    setGranularity,
    disabledDate,
    filterPoints,
    granularities
  }
}

export default useChartRange
